import React, { useState, useEffect } from 'react';
import DatePicker from './DatePicker';
import './SalesActivity.css';
import LoadingState from './LoadingState';
import ConfirmModal from './ConfirmModal';
import { formatNumberWithCommas, formatCurrencyDisplay, getNumberValue, getCurrencyValue, getLocalDateString, toLocalDateString } from '../utils/formatters';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function ApparelInput({ user, managedOwner }) {
  const [allEntries, setAllEntries] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Filter states
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Bulk import states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('upload');
  const [bulkImportError, setBulkImportError] = useState(null);
  const [bulkImportSuccess, setBulkImportSuccess] = useState(null);
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkEntries, setBulkEntries] = useState([]);
  const [bulkFileName, setBulkFileName] = useState('');
  
  const isOwner = user?.role === 'OWNER' || managedOwner;
  const canBulkImport = user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.role === 'ADMIN' || managedOwner;

  useEffect(() => {
    fetchAllData();
  }, [managedOwner]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let dealershipsEndpoint = isOwner 
        ? `${API_URL}/api/owners/dealerships`
        : `${API_URL}/api/dealerships`;
      
      if (managedOwner && isOwner) {
        dealershipsEndpoint += `?ownerId=${managedOwner.id}`;
      }
        
      const dealershipsRes = await fetch(dealershipsEndpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (dealershipsRes.ok) {
        const dealershipsData = await dealershipsRes.json();
        const fetchedDealerships = dealershipsData.dealerships || [];
        setDealerships(fetchedDealerships);
        
        const entriesPromises = fetchedDealerships.map(async (d) => {
          const res = await fetch(
            `${API_URL}/api/dealerships/${d.id}/apparel-licensing`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (res.ok) {
            const data = await res.json();
            return (data.entries || []).map(e => ({
              ...e,
              dealershipId: d.id,
              dealershipName: d.name
            }));
          }
          return [];
        });
        
        const entriesArrays = await Promise.all(entriesPromises);
        const allEntriesFlat = entriesArrays.flat();
        setAllEntries(allEntriesFlat);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewEntry = () => {
    const defaultDealership = dealerships.length > 0 ? dealerships[0].id : '';
    setEditingEntry({
      dealershipId: defaultDealership,
      date: getLocalDateString(),
      transactions: 0,
      items: 0,
      netSales: 0,
      grossProfit: 0,
      notes: ''
    });
    setShowForm(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDeleteEntry = async (id) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/apparel-licensing/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete entry');
      }

      setAllEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
      setAlertModal({ isOpen: true, title: 'Error', message: 'Failed to delete entry' });
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  // Bulk import functions
  const downloadCSVTemplate = () => {
    const headers = ['Date', 'Dealership ID', 'Transactions', 'Items', 'Net Sales', 'Gross Profit', 'Notes'];
    const sample = ['2025-01-15', dealerships[0]?.id || 'DEALERSHIP_ID', '15', '45', '8000.00', '4000.00', 'Optional notes'];
    const csvContent = [headers.join(','), sample.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'apparel_import_template.csv';
    a.click();
  };

  const parseCsvFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('CSV file must contain headers and at least one data row'));
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim());
          const requiredHeaders = ['Date', 'Dealership ID', 'Transactions', 'Items', 'Net Sales', 'Gross Profit'];
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          if (missingHeaders.length > 0) {
            reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
            return;
          }

          const entries = [];
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            const entry = {};
            headers.forEach((header, index) => {
              entry[header.toLowerCase().replace(/ /g, '')] = values[index] || '';
            });
            entries.push({
              date: entry.date,
              dealershipId: entry.dealershipid,
              transactions: entry.transactions,
              items: entry.items,
              netSales: entry.netsales,
              grossProfit: entry.grossprofit,
              notes: entry.notes || ''
            });
          }

          resolve(entries);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const validateBulkEntries = async (entries) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/apparel-sales/bulk-import/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entries })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Validation failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const executeBulkImport = async (entries) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/apparel-sales/bulk-import/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entries })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const handleBulkFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    setBulkImportError(null);

    try {
      const entries = await parseCsvFile(file);
      setBulkEntries(entries);

      const validation = await validateBulkEntries(entries);
      setBulkValidation(validation);
      setBulkStep('review');
    } catch (error) {
      setBulkImportError(error.message);
    }
  };

  const handleBulkExecute = async () => {
    setBulkStep('importing');
    setBulkImportError(null);
    setBulkImportSuccess(null);

    try {
      const result = await executeBulkImport(bulkValidation.normalizedEntries);
      setBulkImportSuccess(`Successfully imported ${result.imported} entries${result.skipped > 0 ? `, skipped ${result.skipped}` : ''}`);
      setBulkStep('done');
      await fetchAllData();
    } catch (error) {
      setBulkImportError(error.message);
      setBulkStep('done');
    }
  };

  const toggleAllDealerships = () => {
    if (selectedDealerships.length === dealerships.length) {
      setSelectedDealerships([]);
    } else {
      setSelectedDealerships(dealerships.map(d => d.id));
    }
  };

  const removeFilter = (type, value) => {
    if (type === 'dealership') {
      setSelectedDealerships(selectedDealerships.filter(d => d !== value));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredEntries = allEntries.filter(entry => {
    if (selectedDealerships.length > 0 && !selectedDealerships.includes(entry.dealershipId)) {
      return false;
    }
    if (searchTerm && !entry.dealershipName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (sortField === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortField === 'netSales' || sortField === 'grossProfit' || sortField === 'transactions' || sortField === 'items') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const activeFilters = [];
  selectedDealerships.forEach(id => {
    const dealership = dealerships.find(d => d.id === id);
    if (dealership) {
      activeFilters.push({ type: 'dealership', value: id, label: dealership.name });
    }
  });

  if (loading) {
    return <LoadingState message="Loading apparel & licensing data..." fullscreen />;
  }

  // Inline form like DailyLogger (appears above table)

  return (
    <div className="sales-activity">
      <div className="sales-activity-header">
        <div>
          <h1>Apparel & Licensing Input</h1>
          <p>Track daily apparel and licensing transactions</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-bar-left">
          <button 
            className={`filter-button ${activeFilters.length > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
            </svg>
            Filters
            {activeFilters.length > 0 && ` (${activeFilters.length})`}
          </button>

          {activeFilters.length > 0 && (
            <div className="active-filters">
              {activeFilters.map((filter, index) => (
                <div key={index} className="active-filter-chip">
                  {filter.label}
                  <button onClick={() => removeFilter(filter.type, filter.value)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {canBulkImport && (
          <div className="bulk-import-container">
            <button 
              className="bulk-import-button"
              onClick={() => {
                setShowBulkModal(true);
                setBulkStep('upload');
                setBulkValidation(null);
                setBulkImportError(null);
                setBulkImportSuccess(null);
                setBulkEntries([]);
                setBulkFileName('');
              }}
              title="Bulk Import"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>
                <path d="M19.4 15a1.6 1.6 0 0 0 .33 1.8l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.8-.33 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.09a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .33-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.09a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.33-1.8l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.8.33h.09a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.09a1.6 1.6 0 0 0 1 1.5h.09a1.6 1.6 0 0 0 1.8-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.33 1.8v.09a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.09a1.6 1.6 0 0 0-1.51 1Z"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-content">
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={toggleAllDealerships} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map(dealership => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="activities-form-container">
          <h2 className="form-heading">{editingEntry?.id ? 'Edit Apparel & Licensing Entry' : 'New Apparel & Licensing Entry'}</h2>
          <ApparelInputForm
            entry={editingEntry}
            dealerships={dealerships}
            onClose={handleCloseForm}
            onSave={() => {
              handleCloseForm();
              fetchAllData();
            }}
          />
        </div>
      )}

      <div className="activities-list">
        <div className="activities-controls">
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search dealership..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {!showForm && (
          <button className="btn-new-sale" onClick={handleNewEntry}>
            + New Entry
          </button>
          )}
        </div>

        {sortedEntries.length === 0 ? (
          <div className="no-activities">
            <p>No entries found</p>
            {allEntries.length > 0 && <p className="filter-hint">Try adjusting your filters</p>}
            <button onClick={handleNewEntry}>Add first entry</button>
          </div>
        ) : (
          <div className="activities-table-container">
            <table className="activities-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('dealershipName')} className="sortable">
                    Dealership {sortField === 'dealershipName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('date')} className="sortable">
                    Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('transactions')} className="sortable">
                    Transactions {sortField === 'transactions' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('items')} className="sortable">
                    Items {sortField === 'items' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('netSales')} className="sortable">
                    NET ($) {sortField === 'netSales' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('grossProfit')} className="sortable">
                    Gross Profit ($) {sortField === 'grossProfit' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.dealershipName}</td>
                    <td>{toLocalDateString(entry.date)}</td>
                    <td>{entry.transactions}</td>
                    <td>{entry.items}</td>
                    <td>${(Math.round(entry.netSales * 100) / 100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                    <td>${(Math.round(entry.grossProfit * 100) / 100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn-edit"
                          onClick={() => handleEditEntry(entry)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteEntry(entry.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmStyle="primary"
        hideCancel={true}
        confirmText="OK"
      />
      </div>
    </div>
  );
}

function ApparelInputForm({ entry, dealerships, onClose, onSave }) {
  const [formData, setFormData] = useState({
    date: entry?.date ? toLocalDateString(entry.date) : getLocalDateString(),
    dealershipId: entry?.dealershipId || '',
    transactions: Number(entry?.transactions) || 0,
    items: Number(entry?.items) || 0,
    netSales: entry?.netSales || 0,
    grossProfit: entry?.grossProfit || 0,
    notes: entry?.notes || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    setFormData({
      date: entry?.date ? toLocalDateString(entry.date) : getLocalDateString(),
      dealershipId: entry?.dealershipId || '',
      transactions: Number(entry?.transactions) || 0,
      items: Number(entry?.items) || 0,
      netSales: entry?.netSales || 0,
      grossProfit: entry?.grossProfit || 0,
      notes: entry?.notes || ''
    });
  }, [entry?.id, entry?.date, entry?.transactions, entry?.items, entry?.netSales, entry?.grossProfit, entry?.notes, entry?.dealershipId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const isEditing = entry?.id;
      const url = isEditing 
        ? `${API_URL}/api/apparel-licensing/${entry.id}`
        : `${API_URL}/api/dealerships/${formData.dealershipId}/apparel-licensing`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: formData.date,
          dealershipId: formData.dealershipId,
          transactions: parseInt(formData.transactions) || 0,
          items: parseInt(formData.items) || 0,
          netSales: parseFloat(formData.netSales) || 0,
          grossProfit: parseFloat(formData.grossProfit) || 0,
          notes: formData.notes || ''
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Entry saved successfully!' });
        setTimeout(onSave, 500);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to save entry' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="entry-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dealership">Dealership</label>
              <select
                id="dealership"
                name="dealershipId"
                value={formData.dealershipId || ''}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">Select a dealership...</option>
                {dealerships.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date</label>
              <DatePicker
                asString
                hideLabel
                className="dp-form"
                selectedDate={formData.date || ''}
                onDateChange={(val) => handleChange({ target: { name: 'date', value: val } })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="transactions">Transactions (NUMBER)</label>
              <input
                type="text"
                id="transactions"
                name="transactions"
                value={formData.transactions}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="items">Items (NUMBER)</label>
              <input
                type="text"
                id="items"
                name="items"
                value={formData.items}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="netSales">Net Sales ($)</label>
              <input
                type="text"
                id="netSales"
                name="netSales"
                value={formData.netSales}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="grossProfit">Gross Profit ($)</label>
              <input
                type="text"
                id="grossProfit"
                name="grossProfit"
                value={formData.grossProfit}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
                className="form-input"
                rows="4"
              />
            </div>
          </div>

          {message.text && (
            <div className={`form-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Saving...' : 'Save Entry'}
            </button>
          </div>
      </form>
    </div>
  );
}

export default ApparelInput;
